import {useCallback, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';

import {AVAIL_STATUS, WF_ACTION_RECEIVER, WF_METHOD, WF_TYPE} from './consts';
import {decryptAES} from './crypto';
import {getUserInfo} from './integration';

import {collectApptNotificationDefaults} from 'Components/NotificationList/notificationLocalUtils.js';
import {WF_DEFAULT_LANG} from 'Components/WorkflowsList/utils.js';
import {useLazyListWorkflowTemplateMessagesQuery} from 'services/schedulerInternal.ts';
import {editAppointmentForCalendar} from 'Store/HostAppointmentsStore';
import {
  fetchUserProfileNameSyncLockSetting,
  fetchUserProfilePicSyncLockSetting,
  patchUserSyncLock,
} from 'Store/HostSettingsStore';
import {selectEnabledNotifMethods} from 'Store/HostWorkflowsStore.js';
import {
  formCustomNotificationsToModel,
  getApptNotificationType,
  modelToFormCustomNotifications,
} from 'Utils/RecurrenceUtils.js';


// profileSyncLock has account level value and user level value, if they don't match,
// we should patch the user level value automatically to make sure it's under account admin's control.
export const useFixProfileSync = () => {
  const dispatch = useDispatch();
  const userProfilePicSyncLock = useSelector((state) => state.hostSettingsState.userProfilePicSyncLock);
  const userProfileNameSyncLock = useSelector((state) => state.hostSettingsState.userProfileNameSyncLock);

  const adminControlUseWebProfile = useSelector((state) => {
    return (state.hostSettingsState.profilePicture.hasLoaded &&
        !state.hostSettingsState.profilePicture.adminProfileSyncLocked) ||
    (state.hostSettingsState.displayName.hasLoaded && !state.hostSettingsState.displayName.adminProfileSyncLocked);
  });
  useEffect(() => {
    dispatch(fetchUserProfilePicSyncLockSetting());
    dispatch(fetchUserProfileNameSyncLockSetting());
  }, [dispatch]);

  useEffect(() => {
    if (adminControlUseWebProfile) {
      if (userProfilePicSyncLock === 'true') {
        dispatch(patchUserSyncLock({
          type: 'pic',
          value: 'false',
        }));
      }
      if (userProfileNameSyncLock === 'true') {
        dispatch(patchUserSyncLock({
          type: 'name',
          value: 'false',
        }));
      }
    }
  }, [dispatch, userProfilePicSyncLock, userProfileNameSyncLock, adminControlUseWebProfile]);
};

export const useDetectZoomLanguage = () => {
  const {i18n} = useTranslation();
  const webPortalLanguage = useSelector((state) => state.integrationState.webPortalLanguage);
  useEffect(() => {
    // _zm_lang is injected by Zoom Client
    if (window.__system_params__?._zm_lang) {
      i18n.changeLanguage(window.__system_params__._zm_lang);
    } else if (webPortalLanguage) {
      i18n.changeLanguage(webPortalLanguage);
    } else {
      const {language} = getUserInfo() || {};
      if (language) {
        i18n.changeLanguage(language);
      }
    }
  }, [i18n, webPortalLanguage]);
};

export const useRecommendAvailableSlots = () => {
  const [searchParams] = useSearchParams();
  const encodedSelectedSlot = searchParams.get('slt');
  const allTimeSlotsStr = searchParams.get('all');

  const apptDays = useSelector((s) => s.bookAppointments.apptDays);

  return useMemo(() => {
    let availableTimeSlots = [];
    let isCurrentSlotAvailable = true;
    if (encodedSelectedSlot) {
      const selectedSlot = decryptAES(encodedSelectedSlot);
      // build a map of `key: time slot ISO string` / `value: available/unavailable`
      const timeSlotAvailabilityMapping = Object.values(apptDays).reduce((res, cur) => res.concat(cur.days || []), [])
        .reduce((res, cur) => res.concat(cur.spots), [])
        .reduce((res, cur) => {
          res[new Date(cur.startTime).toISOString()] = cur.status;
          return res;
        }, {});
      isCurrentSlotAvailable = timeSlotAvailabilityMapping[selectedSlot] !== AVAIL_STATUS.UNAVAILABLE;
      if (allTimeSlotsStr) {
        // all time slots added to email
        const allTimeSlots = decryptAES(allTimeSlotsStr).split(',');
        // exclude the current time slot and unavailable time slots
        availableTimeSlots = allTimeSlots.filter((slot) => {
          return slot !== selectedSlot && timeSlotAvailabilityMapping[slot] !== AVAIL_STATUS.UNAVAILABLE;
        });
      }
    }
    return {availableTimeSlots, isCurrentSlotAvailable};
  }, [apptDays, allTimeSlotsStr, encodedSelectedSlot]);
};
export const SMS_SENDER_SUFFIX = '(via Zoom Scheduler)';
export const useSenderName = () => {
  const {t} = useTranslation();
  const senderName = useSelector((state) => state.hostSettingsState.bookingSenderName);
  const displayName = useSelector((state) => state.hostSettingsState.displayName.value);
  const accountName = useSelector((state) => state.hostSettingsState.accountName);

  const options = useMemo(() => [
    {
      label: `${t('branding.customizeSenderNameHost')} ${SMS_SENDER_SUFFIX}`,
      value: 'Host',
      example: `${displayName} ${SMS_SENDER_SUFFIX}`,
    },
    {
      label: `${t('branding.AccountName')} ${SMS_SENDER_SUFFIX}`,
      value: 'Account',
      example: `${accountName} ${SMS_SENDER_SUFFIX}`,
    },
    {
      // eslint-disable-next-line max-len
      label: `${t('branding.customizeSenderNameHost')} - ${t('branding.AccountName')} ${SMS_SENDER_SUFFIX}`,
      value: 'HostAndAccount',
      example: `${displayName} - ${accountName} ${SMS_SENDER_SUFFIX}`,
    },
  ], [t, displayName, accountName]);

  const currentSenderName = useMemo(() => {
    const idx = options.findIndex((opt) => opt.value === senderName);
    return (options[idx] || options[0]).example;
  }, [options, senderName]);
  return {options, currentSenderName};
};

export const useRevertSmsReminderBody = () => {
  const dispatch = useDispatch();
  const [triggerFetchMessageTemplates] = useLazyListWorkflowTemplateMessagesQuery();
  const enabledNotifMethods = useSelector(selectEnabledNotifMethods);
  const revertSmsReminderBody = useCallback(async (latestAppt, actingScope) => {
    // check and auto revert appt sms reminder body to default template
    try {
      const currentSmsReminderBody = latestAppt?.customNotifications?.attendeeSmsReminder?.body;
      const {templates} = await triggerFetchMessageTemplates(latestAppt?.language ?? WF_DEFAULT_LANG, true).unwrap();
      const defaultSmsReminderBody = templates.find((t) =>
        t.variant === WF_TYPE.REMINDER &&
        t.receiver === WF_ACTION_RECEIVER.BOOKER &&
        t.method === WF_METHOD.TEXT
      ).body;
      if (process.env.REACT_APP_ENABLE_CUSTOM_SMS !== 'true' && currentSmsReminderBody &&
        currentSmsReminderBody !== defaultSmsReminderBody) {
        const newData = formCustomNotificationsToModel(
          getApptNotificationType(latestAppt), modelToFormCustomNotifications(
            latestAppt,
            collectApptNotificationDefaults(templates),
            enabledNotifMethods
          ), templates);
        newData.attendeeSmsReminder.body = defaultSmsReminderBody;
        const editedAppt = await dispatch(editAppointmentForCalendar({
          asScope: actingScope,
          apptId: latestAppt.id,
          data: {
            customNotifications: newData,
          },
        })).unwrap();
        return editedAppt;
      }
    } catch (updateError) {
      console.error('failed to auto revert default sms template', updateError);
    }
  }, [dispatch, enabledNotifMethods, triggerFetchMessageTemplates]);
  return revertSmsReminderBody;
};
